import { useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { MailTextEditor } from './MailTextEditor';
import { useMailFrame } from './useMailFrame';
import { isStandardText, mailChecks, seriesChecks } from '../../core/rules/mail';
import { plural } from '../../core/format/text';
import type { MailSettingsState } from './useMailSettings';
import type { MailGroup, MailSeries, MailSettings, MailTemplate } from '../../core/types/mail';
const groups: {
  key: MailGroup;
  label: string;
}[] = [{
  key: 'bestelling',
  label: 'Bestelling'
}, {
  key: 'verzending',
  label: 'Verzending'
}, {
  key: 'afspraken',
  label: 'Afspraken'
}, {
  key: 'cadeaubon',
  label: 'Cadeaubon'
}, {
  key: 'abonnement',
  label: 'Abonnement'
}, {
  key: 'account',
  label: 'Account'
}];
const rowCls = 'flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors duration-150 ease-soft hover:bg-shell focus-visible:bg-shell sm:px-5';
const waitText = (s: MailSeries) => s.wait === 0 ? 'direct' : `na ${plural(s.wait, s.waitUnit === 'uren' ? 'uur' : 'dag', s.waitUnit === 'uren' ? 'uur' : 'dagen')}`;

/** Tabblad Automatische mails: per groep de mails die vanzelf gaan, daaronder de reeksen. */
export function AutomaticMails({
  state,
  settings
}: {
  state: MailSettingsState;
  settings: MailSettings;
}) {
  const [params, setParams] = useSearchParams();
  const open = params.get('mail');
  const frame = useMailFrame(settings.brand);
  const item = settings.templates.find(m => m.key === open) ?? settings.series.find(m => m.key === open);
  const go = (key: string | null) => setParams(p => {
    const next = new URLSearchParams(p);
    if (key) next.set('mail', key);else next.delete('mail');
    return next;
  }, {
    replace: false
  });
  if (item) {
    const isSeries = 'wait' in item;
    return <div data-id="element-2679" data-mp="vd3nsyxja08e">
        <button type="button" onClick={() => go(null)} className="mb-4 inline-flex min-h-[44px] items-center gap-1.5 text-[14px] text-ink hover:text-clay sm:min-h-0" data-id="element-2680" data-mp="v9pltrss7t1h">
          <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2681" data-mp="79dtbabhxx2q" />
          Alle mails
        </button>
        <MailTextEditor item={item} onChange={patch => isSeries ? state.setSeries(item.key, patch) : state.setTemplate(item.key, patch)} onTest={() => state.sendTest(item.key)} frame={frame} from={settings.brand.senderName} dirty={state.dirty} data-id="element-2682" data-mp="2fjeolar08ai" />
      </div>;
  }
  const row = (m: MailTemplate | MailSeries, sub: string) => {
    const errors = ('wait' in m ? seriesChecks(m) : mailChecks(m)).filter(c => c.level === 'error').length;
    const required = !('wait' in m) && m.required;
    return <li key={m.key} data-id="element-2683" data-mp="1dv80h9c9n19q">
        <button type="button" onClick={() => go(m.key)} className={rowCls} data-id="element-2684" data-mp="1dvrgulm9phln">
          <span className="min-w-0 flex-1" data-id="element-2685" data-mp="18yrae9liacu0">
            <span className="block truncate text-[14px] text-ink" data-id="element-2686" data-mp="1ggyy74fde6n3">{m.name}</span>
            <span className="block truncate text-[13px] text-muted" data-id="element-2687" data-mp="1geuxevcrpnr1">{sub}</span>
          </span>
          {errors > 0 ? <Badge tone="accent" data-id="element-2688" data-mp="1q6sc4c2zixui">{plural(errors, 'punt', 'punten')}</Badge> : required ? <Badge tone="neutral" data-id="element-2689" data-mp="1v5log16jcit3">Verplicht</Badge> : m.enabled ? <Badge tone="success" data-id="element-2690" data-mp="1v7oc4ek7tzun">Aan</Badge> : <Badge tone="neutral" data-id="element-2691" data-mp="xjej2p2eqmfo">Uit</Badge>}
          {!isStandardText(m) && <span className="hidden text-[12px] text-muted sm:inline" data-id="element-2692" data-mp="12gefe89ggatl">eigen tekst</span>}
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2693" data-mp="17frcrmxyd0af" />
        </button>
      </li>;
  };
  return <div className="space-y-6" data-id="element-2694" data-mp="1bj77ueudisl0">
      <p className="max-w-2xl text-[14px] leading-relaxed text-muted" data-id="element-2695" data-mp="2ivpra6bwgej1">
        Deze mails gaan vanzelf, bij een bestelling, afspraak of account. Opmaak en wanneer ze gaan regelt de server; de woorden kies jij. Laat je een tekst leeg,
        dan gaat de standaardtekst.
      </p>
      <div className="grid gap-6 lg:grid-cols-2" data-id="element-2696" data-mp="2lhp3fxqryx9p">
        {groups.map(g => {
        const items = settings.templates.filter(m => m.group === g.key);
        if (!items.length) return null;
        return <Panel key={g.key} flush title={g.label} data-id="element-2697" data-mp="2qf8gwvfnf60l">
              <ul className="divide-y divide-line" data-id="element-2698" data-mp="2qddwrjc7bhxk">{items.map(m => row(m, m.trigger))}</ul>
            </Panel>;
      })}
      </div>
      <Panel flush title="Reeksen" description="Mails die volgen op wat een klant deed, of juist niet deed. Alleen naar wie toestemming gaf." data-id="element-2699" data-mp="2zfwu5nx7x2cf">
        <ul className="divide-y divide-line" data-id="element-2700" data-mp="3e3881wgvgyfp">{settings.series.map(s => row(s, `${s.trigger}, ${waitText(s)}`))}</ul>
      </Panel>
    </div>;
}